import React, { useState, useEffect, useMemo } from 'react'
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Eye, 
  Clock, 
  Monitor, 
  Smartphone, 
  Calendar,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  Target,
  Lightbulb,
  ArrowUp,
  ArrowDown,
  Minus,
  Brain,
  Zap,
  Tv,
  Crown,
  Star,
  CreditCard,
  DollarSign,
  PieChart,
  BarChart,
  LineChart
} from 'lucide-react'
import { useWatchedDeviceService } from '@/hooks/useWatchedDeviceService'
import { useWatchedScreenService } from '@/hooks/useWatchedScreenService'
import { useAuth } from '@/contexts/AuthContext'
import { isFreePlanUser, hasActiveSubscription, getDeviceLimit, getScreenLimit } from '@/utils/subscriptionUtils'
import SubscriptionUpgradeModal from '@/components/UI/SubscriptionUpgradeModal'
import useGoogleAnalytics from '@/hooks/useGoogleAnalytics'
import channelService from '@/utils/services/ChannelService.js'
import SubscriptionService from '@/utils/services/SubscriptionService'

const Analytics = () => {
  const { currentUser, currentUserData } = useAuth()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const { devices, loading: devicesLoading } = useWatchedDeviceService()
  const { screens, loading: screensLoading, refreshScreens } = useWatchedScreenService()
  const [selectedPeriod, setSelectedPeriod] = useState('7d')
  const [activeTab, setActiveTab] = useState('overview')
  const [isGeneratingReports, setIsGeneratingReports] = useState(true)
  const [loadingStep, setLoadingStep] = useState(0)
  const [channels, setChannels] = useState([])
  const [channelsLoading, setChannelsLoading] = useState(true)
  const [userSubscriptions, setUserSubscriptions] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  
  // Google Analytics tracking
  const { trackAnalyticsView } = useGoogleAnalytics()

  // Check if user is on free plan and show upgrade modal
  useEffect(() => {
    if (isFreePlanUser(currentUserData)) {
      setShowUpgradeModal(true)
    }
  }, [currentUserData])

  // Load channels and subscription data
  useEffect(() => {
    const loadData = async () => {
      try {
        setChannelsLoading(true)
        
        // Load channels
        const channelsData = await channelService.getAllChannels()
        setChannels(channelsData)
        
        // Load subscription data
        const subscriptionData = await SubscriptionService.getSubscriptions()
        setSubscriptions(subscriptionData)
        
        // Load user subscriptions
        const userSubs = await SubscriptionService.getUserSubscriptions()
        setUserSubscriptions(userSubs)
        
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        setChannelsLoading(false)
      }
    }
    
    loadData()
  }, [])

  // AI loading messages
  const loadingMessages = [
    "🤖 AI Agent initializing...",
    "📊 Analyzing device performance data...",
    "📈 Processing content distribution metrics...",
    "📺 Calculating channel usage statistics...",
    "💳 Analyzing subscription plan utilization...",
    "⏰ Calculating schedule optimization insights...",
    "🎯 Generating personalized recommendations...",
    "✨ Finalizing your business intelligence report..."
  ]

  // Track analytics page view and refresh data
  useEffect(() => {
    trackAnalyticsView('customer_analytics')
  }, [trackAnalyticsView])

  // Simulate AI processing with different steps
  useEffect(() => {
    if (devicesLoading || screensLoading || channelsLoading) return

    const steps = [
      { delay: 1000, message: 0 },
      { delay: 1500, message: 1 },
      { delay: 1200, message: 2 },
      { delay: 1400, message: 3 },
      { delay: 1300, message: 4 },
      { delay: 1800, message: 5 },
      { delay: 1400, message: 6 },
      { delay: 2000, message: 7 }
    ]

    let currentStep = 0
    const totalDelay = steps.reduce((sum, step) => sum + step.delay, 0)

    const stepTimer = setInterval(() => {
      if (currentStep < steps.length) {
        setLoadingStep(steps[currentStep].message)
        currentStep++
      } else {
        clearInterval(stepTimer)
        setIsGeneratingReports(false)
      }
    }, 1000)

    // Fallback to ensure loading stops after total delay
    const fallbackTimer = setTimeout(() => {
      setIsGeneratingReports(false)
    }, totalDelay + 2000)

    return () => {
      clearInterval(stepTimer)
      clearTimeout(fallbackTimer)
    }
  }, [devicesLoading, screensLoading, channelsLoading])

  // Calculate analytics data
  const analyticsData = useMemo(() => {
    if (!devices || !screens) return null

    // Device analytics
    const totalDevices = devices.length
    const onlineDevices = devices.filter(device => 
      device.lastAccess && (Date.now() - device.lastAccess < 360000)
    ).length
    const offlineDevices = totalDevices - onlineDevices
    const deviceUptime = totalDevices > 0 ? (onlineDevices / totalDevices) * 100 : 0

    // Screen analytics
    const totalScreens = screens.length
    const dynamicScreens = screens.filter(screen => screen.isDynamic).length
    const imageScreens = screens.filter(screen => screen.imageUrl && !screen.isDynamic).length
    const videoScreens = screens.filter(screen => screen.videoUrl && !screen.isDynamic).length
    const slideshowScreens = screens.filter(screen => screen.slidesData && screen.slidesData.length > 0 && !screen.isDynamic).length

    // Channel analytics
    const totalChannels = channels.length
    const activeChannels = channels.filter(channel => channel.isActive).length
    const mutedChannels = channels.filter(channel => channel.isMuted).length
    const devicesUsingChannels = devices.filter(device => device.channelId && device.channelId !== '').length
    const devicesUsingScreens = devices.filter(device => device.screenId && device.screenId !== '' && (!device.channelId || device.channelId === '')).length

    // Subscription analytics
    const isOnFreePlan = isFreePlanUser(currentUserData)
    const hasSubscription = hasActiveSubscription(currentUserData)
    const deviceLimit = getDeviceLimit(currentUserData, userSubscriptions, subscriptions)
    const screenLimit = getScreenLimit(currentUserData, userSubscriptions, subscriptions)
    const deviceUtilization = deviceLimit > 0 ? (totalDevices / deviceLimit) * 100 : 0
    const screenUtilization = screenLimit > 0 ? (totalScreens / screenLimit) * 100 : 0

    // Schedule analytics
    const allSchedules = devices.flatMap(device => device.schedule || [])
    const activeSchedules = allSchedules.filter(schedule => {
      if (!schedule) return false
      const now = new Date()
      if (schedule.type === 'regular') {
        const startDate = new Date(schedule.startDate)
        const endDate = new Date(schedule.endDate)
        return now >= startDate && now <= endDate
      }
      return true // repetitive schedules are always considered active
    }).length

    const regularSchedules = allSchedules.filter(s => s?.type === 'regular').length
    const repetitiveSchedules = allSchedules.filter(s => s?.type === 'repetitive').length

    // Performance metrics
    const devicesWithIssues = devices.filter(device => {
      const lastAccess = device.lastAccess
      return !lastAccess || (Date.now() - lastAccess > 86400000) // 24 hours
    }).length

    const contentDistribution = {
      images: imageScreens,
      videos: videoScreens,
      slideshows: slideshowScreens,
      dynamic: dynamicScreens
    }

    // Content strategy metrics
    const contentTypesUsed = Object.values(contentDistribution).filter(count => count > 0).length
    const averageContentPerDevice = totalDevices > 0 ? (totalScreens + totalChannels) / totalDevices : 0
    const contentDiversity = totalScreens > 0 ? contentTypesUsed / 4 : 0 // 4 types: images, videos, slideshows, dynamic

    return {
      devices: {
        total: totalDevices,
        online: onlineDevices,
        offline: offlineDevices,
        uptime: deviceUptime,
        issues: devicesWithIssues,
        limit: deviceLimit,
        utilization: deviceUtilization
      },
      screens: {
        total: totalScreens,
        distribution: contentDistribution,
        types: {
          images: imageScreens,
          videos: videoScreens,
          slideshows: slideshowScreens,
          dynamic: dynamicScreens
        },
        limit: screenLimit,
        utilization: screenUtilization
      },
      channels: {
        total: totalChannels,
        active: activeChannels,
        muted: mutedChannels,
        utilization: totalDevices > 0 ? (devicesUsingChannels / totalDevices) * 100 : 0
      },
      content: {
        devicesUsingChannels,
        devicesUsingScreens,
        contentTypesUsed,
        averageContentPerDevice,
        contentDiversity
      },
      subscription: {
        isOnFreePlan,
        hasSubscription,
        planType: isOnFreePlan ? 'Free' : hasSubscription ? 'Premium' : 'Free',
        deviceLimit,
        screenLimit
      },
      schedules: {
        total: allSchedules.length,
        active: activeSchedules,
        regular: regularSchedules,
        repetitive: repetitiveSchedules
      }
    }
  }, [devices, screens, channels, currentUserData, userSubscriptions, subscriptions])

  // Generate recommendations
  const recommendations = useMemo(() => {
    if (!analyticsData) return []

    const recs = []

    // Device recommendations
    if (analyticsData.devices.uptime < 80) {
      recs.push({
        type: 'warning',
        icon: AlertTriangle,
        title: 'Low Device Uptime',
        description: `${analyticsData.devices.uptime.toFixed(1)}% of devices are online. Consider checking device connections and network stability.`,
        action: 'Review device status and network connectivity'
      })
    }

    if (analyticsData.devices.issues > 0) {
      recs.push({
        type: 'error',
        icon: XCircle,
        title: 'Devices Need Attention',
        description: `${analyticsData.devices.issues} device(s) haven't been seen in over 24 hours.`,
        action: 'Check device power and internet connections'
      })
    }

    // Channel recommendations
    if (analyticsData.channels.total === 0) {
      recs.push({
        type: 'info',
        icon: Tv,
        title: 'No Channels Created',
        description: 'Channels provide live content streaming to multiple devices simultaneously.',
        action: 'Create your first channel for live content distribution'
      })
    } else if (analyticsData.channels.active === 0) {
      recs.push({
        type: 'warning',
        icon: AlertTriangle,
        title: 'No Active Channels',
        description: 'You have channels but none are currently active.',
        action: 'Activate your channels to start streaming content'
      })
    }

    // Subscription recommendations
    if (analyticsData.subscription.isOnFreePlan) {
      recs.push({
        type: 'info',
        icon: Crown,
        title: 'Upgrade Your Plan',
        description: 'You\'re on the free plan. Upgrade to unlock unlimited devices, screens, and advanced features.',
        action: 'Explore premium plans for enhanced capabilities'
      })
    } else if (analyticsData.devices.utilization > 80) {
      recs.push({
        type: 'info',
        icon: TrendingUp,
        title: 'High Device Utilization',
        description: `You're using ${analyticsData.devices.utilization.toFixed(1)}% of your device limit. Consider upgrading for more capacity.`,
        action: 'Upgrade your plan to add more devices'
      })
    }

    // Content recommendations
    if (analyticsData.screens.total === 0 && analyticsData.channels.total === 0) {
      recs.push({
        type: 'info',
        icon: Lightbulb,
        title: 'No Content Created',
        description: 'Start creating content to engage your audience.',
        action: 'Create your first screen or channel content'
      })
    } else if (analyticsData.content.contentDiversity < 0.5) {
      recs.push({
        type: 'info',
        icon: Lightbulb,
        title: 'Diversify Your Content',
        description: 'Consider creating different types of content to keep your audience engaged.',
        action: 'Add more diverse content types (images, videos, slideshows, dynamic)'
      })
    }

    // Schedule recommendations
    if (analyticsData.schedules.total === 0) {
      recs.push({
        type: 'info',
        icon: Calendar,
        title: 'No Schedules Set',
        description: 'Set up schedules to automatically display your content at optimal times.',
        action: 'Create your first content schedule'
      })
    } else if (analyticsData.schedules.repetitive === 0) {
      recs.push({
        type: 'info',
        icon: Calendar,
        title: 'Consider Repetitive Schedules',
        description: 'Repetitive schedules can help maintain consistent content display.',
        action: 'Set up repetitive schedules for regular content'
      })
    }

    // Performance recommendations
    if (analyticsData.devices.total > 0 && analyticsData.content.averageContentPerDevice < 1) {
      recs.push({
        type: 'info',
        icon: Target,
        title: 'Optimize Content Distribution',
        description: 'Consider creating more content to maximize your device utilization.',
        action: 'Create additional content for better device utilization'
      })
    }

    return recs
  }, [analyticsData])

  // Calculate trends (simplified - in real implementation, you'd compare with historical data)
  const trends = useMemo(() => {
    if (!analyticsData) return {}

    return {
      devices: {
        trend: 'stable',
        change: 0,
        direction: Minus
      },
      screens: {
        trend: 'stable',
        change: 0,
        direction: Minus
      },
      channels: {
        trend: 'stable',
        change: 0,
        direction: Minus
      },
      schedules: {
        trend: 'stable',
        change: 0,
        direction: Minus
      }
    }
  }, [analyticsData])

  // Show AI loading screen
  if (isGeneratingReports || devicesLoading || screensLoading || channelsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600">AI-powered business intelligence</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center py-16">
            {/* AI Brain Icon with Animation */}
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full opacity-20 animate-pulse"></div>
              <div className="relative bg-gradient-to-r from-blue-500 to-purple-600 rounded-full p-6 w-24 h-24 mx-auto flex items-center justify-center">
                <Brain className="w-12 h-12 text-white" />
              </div>
            </div>

            {/* Loading Message */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                AI Agent Generating Your Reports
              </h3>
              <p className="text-gray-600 text-lg">
                {loadingMessages[loadingStep] || loadingMessages[loadingMessages.length - 1]}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="w-full max-w-md mx-auto mb-6">
              <div className="bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-1000 ease-out"
                  style={{ 
                    width: `${((loadingStep + 1) / loadingMessages.length) * 100}%` 
                  }}
                ></div>
              </div>
            </div>

            {/* Animated Dots */}
            <div className="flex justify-center space-x-2">
              {[0, 1, 2].map((dot) => (
                <div
                  key={dot}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    dot <= loadingStep % 3 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600' 
                      : 'bg-gray-300'
                  }`}
                  style={{
                    animationDelay: `${dot * 200}ms`,
                    animation: dot <= loadingStep % 3 ? 'bounce 1s infinite' : 'none'
                  }}
                ></div>
              ))}
            </div>

            {/* AI Processing Indicators */}
            <div className="mt-8 grid grid-cols-5 gap-4 max-w-lg mx-auto">
              <div className="text-center">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Monitor className="w-4 h-4 text-blue-600" />
                </div>
                <p className="text-xs text-gray-500">Devices</p>
              </div>
              <div className="text-center">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Eye className="w-4 h-4 text-purple-600" />
                </div>
                <p className="text-xs text-gray-500">Screens</p>
              </div>
              <div className="text-center">
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Tv className="w-4 h-4 text-indigo-600" />
                </div>
                <p className="text-xs text-gray-500">Channels</p>
              </div>
              <div className="text-center">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Crown className="w-4 h-4 text-yellow-600" />
                </div>
                <p className="text-xs text-gray-500">Plans</p>
              </div>
              <div className="text-center">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Calendar className="w-4 h-4 text-orange-600" />
                </div>
                <p className="text-xs text-gray-500">Schedules</p>
              </div>
            </div>

            {/* Fun AI Message */}
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg max-w-md mx-auto">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Zap className="w-4 h-4 text-yellow-500" />
                <span className="font-medium">AI Tip:</span>
                <span>Crunching numbers to find hidden insights...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!analyticsData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600">View insights and performance data</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No data available</h3>
            <p className="text-gray-600">Start creating content and connecting devices to see analytics</p>
          </div>
        </div>
      </div>
    )
  }

  // If user is on free plan, show upgrade modal and limited content
  if (isFreePlanUser(currentUserData)) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600">Business insights and performance data</p>
          </div>
        </div>

        {/* Limited content for free users */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Analytics Unavailable</h3>
            <p className="text-gray-600 mb-4">Upgrade to access detailed analytics and insights</p>
            <div className="text-sm text-gray-500">
              <p>• Performance metrics and trends</p>
              <p>• Device and channel utilization analytics</p>
              <p>• Content engagement insights</p>
              <p>• Subscription plan optimization</p>
              <p>• AI-powered recommendations</p>
            </div>
          </div>
        </div>
        
        {/* Subscription Upgrade Modal */}
        <SubscriptionUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="analytics"
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Business insights and performance data</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'devices', label: 'Devices', icon: Monitor },
              { id: 'screens', label: 'Screens', icon: Eye },
              { id: 'channels', label: 'Channels', icon: Tv },
              { id: 'subscription', label: 'Subscription', icon: Crown },
              { id: 'schedules', label: 'Schedules', icon: Calendar },
              { id: 'recommendations', label: 'Recommendations', icon: Lightbulb }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                  title="Total Devices"
                  value={analyticsData.devices.total}
                  subtitle={`${analyticsData.devices.utilization.toFixed(1)}% of limit`}
                  icon={Monitor}
                  trend={trends.devices}
                  color="blue"
                />
                <MetricCard
                  title="Online Devices"
                  value={analyticsData.devices.online}
                  subtitle={`${analyticsData.devices.uptime.toFixed(1)}% uptime`}
                  icon={CheckCircle}
                  color="green"
                />
                <MetricCard
                  title="Total Screens"
                  value={analyticsData.screens.total}
                  subtitle={`${analyticsData.screens.utilization.toFixed(1)}% of limit`}
                  icon={Eye}
                  trend={trends.screens}
                  color="purple"
                />
                <MetricCard
                  title="Active Channels"
                  value={analyticsData.channels.active}
                  subtitle={`of ${analyticsData.channels.total} total`}
                  icon={Tv}
                  trend={trends.channels}
                  color="indigo"
                />
              </div>

              {/* Quick Insights */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Distribution</h3>
                  <div className="space-y-3">
                    {Object.entries(analyticsData.screens.distribution).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 capitalize">{type}</span>
                        <span className="text-sm text-gray-900 font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Strategy</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Devices Using Screens</span>
                      <span className="text-sm text-gray-900 font-semibold">{analyticsData.content.devicesUsingScreens}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Devices Using Channels</span>
                      <span className="text-sm text-gray-900 font-semibold">{analyticsData.content.devicesUsingChannels}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Content Diversity</span>
                      <span className="text-sm text-gray-900 font-semibold">{(analyticsData.content.contentDiversity * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Status</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Plan Type</span>
                      <span className="text-sm text-gray-900 font-semibold">{analyticsData.subscription.planType}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Device Limit</span>
                      <span className="text-sm text-gray-900 font-semibold">{analyticsData.subscription.deviceLimit === Infinity ? 'Unlimited' : analyticsData.subscription.deviceLimit}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Screen Limit</span>
                      <span className="text-sm text-gray-900 font-semibold">{analyticsData.subscription.screenLimit === Infinity ? 'Unlimited' : analyticsData.subscription.screenLimit}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Devices Tab */}
          {activeTab === 'devices' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Monitor className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Device Status</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Online</span>
                      <span className="text-sm font-semibold text-green-600">{analyticsData.devices.online}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Offline</span>
                      <span className="text-sm font-semibold text-red-600">{analyticsData.devices.offline}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Uptime</span>
                      <span className="text-sm font-semibold text-blue-600">{analyticsData.devices.uptime.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Issues</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Devices with Issues</span>
                      <span className="text-sm font-semibold text-orange-600">{analyticsData.devices.issues}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Health Score</span>
                      <span className="text-sm font-semibold text-green-600">
                        {analyticsData.devices.total > 0 ? Math.round(((analyticsData.devices.total - analyticsData.devices.issues) / analyticsData.devices.total) * 100) : 100}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Activity className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Performance</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Average Response</span>
                      <span className="text-sm font-semibold text-green-600">Good</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Network Status</span>
                      <span className="text-sm font-semibold text-green-600">Stable</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Screens Tab */}
          {activeTab === 'screens' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-purple-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Eye className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Content Overview</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Screens</span>
                      <span className="text-sm font-semibold text-purple-600">{analyticsData.screens.total}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Images</span>
                      <span className="text-sm font-semibold text-gray-900">{analyticsData.screens.types.images}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Videos</span>
                      <span className="text-sm font-semibold text-gray-900">{analyticsData.screens.types.videos}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Slideshows</span>
                      <span className="text-sm font-semibold text-gray-900">{analyticsData.screens.types.slideshows}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Dynamic Content</span>
                      <span className="text-sm font-semibold text-gray-900">{analyticsData.screens.types.dynamic}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Target className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Content Strategy</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Content per Device</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {analyticsData.content.averageContentPerDevice.toFixed(1)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Most Popular Type</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {Object.entries(analyticsData.screens.distribution).reduce((a, b) => a[1] > b[1] ? a : b)[0]}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Content Diversity</span>
                      <span className="text-sm font-semibold text-green-600">
                        {(analyticsData.content.contentDiversity * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Channels Tab */}
          {activeTab === 'channels' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-indigo-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Tv className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Channel Overview</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Channels</span>
                      <span className="text-sm font-semibold text-indigo-600">{analyticsData.channels.total}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Active Channels</span>
                      <span className="text-sm font-semibold text-green-600">{analyticsData.channels.active}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Muted Channels</span>
                      <span className="text-sm font-semibold text-orange-600">{analyticsData.channels.muted}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Monitor className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Device Usage</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Using Channels</span>
                      <span className="text-sm font-semibold text-green-600">{analyticsData.content.devicesUsingChannels}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Using Screens</span>
                      <span className="text-sm font-semibold text-blue-600">{analyticsData.content.devicesUsingScreens}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Channel Utilization</span>
                      <span className="text-sm font-semibold text-indigo-600">{analyticsData.channels.utilization.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Activity className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Performance</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Activation Rate</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {analyticsData.channels.total > 0 ? Math.round((analyticsData.channels.active / analyticsData.channels.total) * 100) : 0}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Average Devices per Channel</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {analyticsData.channels.total > 0 ? (analyticsData.content.devicesUsingChannels / analyticsData.channels.total).toFixed(1) : 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Content Strategy</span>
                      <span className="text-sm font-semibold text-green-600">
                        {analyticsData.content.devicesUsingChannels > analyticsData.content.devicesUsingScreens ? 'Channel-Focused' : 'Screen-Focused'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-yellow-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Crown className="w-5 h-5 text-yellow-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Plan Status</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Current Plan</span>
                      <span className="text-sm font-semibold text-yellow-600">{analyticsData.subscription.planType}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Subscription Status</span>
                      <span className="text-sm font-semibold text-green-600">
                        {analyticsData.subscription.hasSubscription ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Plan Features</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {analyticsData.subscription.isOnFreePlan ? 'Basic' : 'Premium'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Monitor className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Device Limits</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Device Limit</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {analyticsData.subscription.deviceLimit === Infinity ? 'Unlimited' : analyticsData.subscription.deviceLimit}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Current Usage</span>
                      <span className="text-sm font-semibold text-gray-900">{analyticsData.devices.total}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Utilization</span>
                      <span className="text-sm font-semibold text-green-600">{analyticsData.devices.utilization.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Eye className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Screen Limits</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Screen Limit</span>
                      <span className="text-sm font-semibold text-purple-600">
                        {analyticsData.subscription.screenLimit === Infinity ? 'Unlimited' : analyticsData.subscription.screenLimit}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Current Usage</span>
                      <span className="text-sm font-semibold text-gray-900">{analyticsData.screens.total}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Utilization</span>
                      <span className="text-sm font-semibold text-green-600">{analyticsData.screens.utilization.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Upgrade Recommendations */}
              {analyticsData.subscription.isOnFreePlan && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Star className="w-5 h-5 text-yellow-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Upgrade Benefits</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900">Current Limitations:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Limited to 1 device</li>
                        <li>• Basic screen types only</li>
                        <li>• No advanced scheduling</li>
                        <li>• No AI features</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900">Premium Features:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Unlimited devices</li>
                        <li>• All content types</li>
                        <li>• Advanced scheduling</li>
                        <li>• AI-powered features</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Schedules Tab */}
          {activeTab === 'schedules' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-orange-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Calendar className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Schedule Overview</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Schedules</span>
                      <span className="text-sm font-semibold text-orange-600">{analyticsData.schedules.total}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Active Schedules</span>
                      <span className="text-sm font-semibold text-green-600">{analyticsData.schedules.active}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Activation Rate</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {analyticsData.schedules.total > 0 ? Math.round((analyticsData.schedules.active / analyticsData.schedules.total) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Clock className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Schedule Types</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Regular</span>
                      <span className="text-sm font-semibold text-gray-900">{analyticsData.schedules.regular}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Repetitive</span>
                      <span className="text-sm font-semibold text-gray-900">{analyticsData.schedules.repetitive}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Most Used</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {analyticsData.schedules.regular > analyticsData.schedules.repetitive ? 'Regular' : 'Repetitive'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Activity className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Efficiency</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Schedules per Device</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {analyticsData.devices.total > 0 ? (analyticsData.schedules.total / analyticsData.devices.total).toFixed(1) : 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Coverage</span>
                      <span className="text-sm font-semibold text-green-600">
                        {analyticsData.devices.total > 0 ? Math.round((analyticsData.schedules.total / analyticsData.devices.total) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations Tab */}
          {activeTab === 'recommendations' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                {recommendations.length === 0 ? (
                  <div className="bg-green-50 rounded-xl p-6 text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">All Good!</h3>
                    <p className="text-gray-600">Your digital signage setup is performing well. Keep up the great work!</p>
                  </div>
                ) : (
                  recommendations.map((rec, index) => (
                    <div key={index} className={`rounded-xl p-6 border-l-4 ${
                      rec.type === 'error' ? 'bg-red-50 border-red-500' :
                      rec.type === 'warning' ? 'bg-orange-50 border-orange-500' :
                      'bg-blue-50 border-blue-500'
                    }`}>
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${
                          rec.type === 'error' ? 'bg-red-100' :
                          rec.type === 'warning' ? 'bg-orange-100' :
                          'bg-blue-100'
                        }`}>
                          <rec.icon className={`w-5 h-5 ${
                            rec.type === 'error' ? 'text-red-600' :
                            rec.type === 'warning' ? 'text-orange-600' :
                            'text-blue-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{rec.title}</h3>
                          <p className="text-gray-600 mb-3">{rec.description}</p>
                          <div className="text-sm font-medium text-gray-700">
                            💡 <span className="font-semibold">Recommendation:</span> {rec.action}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Future Opportunities */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Lightbulb className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Future Opportunities</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">📈 Scale Your Network</h4>
                    <p className="text-sm text-gray-600">Consider adding more devices to expand your digital signage reach.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">📺 Channel Strategy</h4>
                    <p className="text-sm text-gray-600">Use channels for live content streaming to multiple devices simultaneously.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">🎯 Optimize Content</h4>
                    <p className="text-sm text-gray-600">Use analytics to identify your best-performing content and create more of it.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">💳 Plan Optimization</h4>
                    <p className="text-sm text-gray-600">Monitor your usage to optimize your subscription plan and costs.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">⏰ Smart Scheduling</h4>
                    <p className="text-sm text-gray-600">Implement time-based content strategies to maximize audience engagement.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">🔄 Content Rotation</h4>
                    <p className="text-sm text-gray-600">Regularly update your content to keep your audience engaged and informed.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-100',
    green: 'text-green-600 bg-green-100',
    purple: 'text-purple-600 bg-purple-100',
    orange: 'text-orange-600 bg-orange-100',
    red: 'text-red-600 bg-red-100'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <div className="flex items-center gap-1">
            <trend.direction className={`w-4 h-4 ${
              trend.trend === 'up' ? 'text-green-500' :
              trend.trend === 'down' ? 'text-red-500' :
              'text-gray-400'
            }`} />
            <span className={`text-xs font-medium ${
              trend.trend === 'up' ? 'text-green-600' :
              trend.trend === 'down' ? 'text-red-600' :
              'text-gray-500'
            }`}>
              {trend.change}%
            </span>
          </div>
        )}
      </div>
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  )
}

export default Analytics 